import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShareIcon } from "@heroicons/react/24/outline";
//import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageLoading from "../../components/PageLoading";
import CommentSection from "../../components/CommentSection";
import ListVoteBar from "../../components/ListVoteBar";
import ListItemVoteBar from "../../components/ListItemVoteBar";
import ListScoreBadge from "../../components/ListScoreBadge";
import { GetUsersListById } from "../../api-requests/Get";
//import { UserContext } from "../../context/UserContext";
import { formatNumber } from "../../helpers/NumberFormats";
import { Helmet } from "react-helmet-async";
import { createSlug } from "../../helpers/SlugHelper";
import { defaultpfp, generations } from "../../constants/constants";
import { getListScoreLabel } from "../../helpers/ListScoreCalculations";
import {
  ListVoteType,
  ListVoteTotals,
  ListVoteEnhancedTotals,
  ListVotePercentages,
  ListItemVoteType,
  ListItemVoteTotals,
} from "../../types/votes";
import ListEngagement from "../../components/ListEngagement";

interface ListData {
  id: string;
  title: string;
  description?: string;
  created: string;
  reactions: number;
  views: number;
  engagement?: {
    reactionCount: number;
    engagementScore: number;
    listVotes: number;
    listItemVotes: number;
    comments: number;
  };
  // Enhanced score data
  listScore?: number;
  listScoreLabel?: string;
  listVoteTotals?: ListVoteEnhancedTotals;
  listVotePercentages?: ListVotePercentages;
  currentUserListVote?: ListVoteType | null;
  // Backward compatibility
  voteTotals?: ListVoteTotals;
  currentUserVote?: ListVoteType | null;
  user: {
    id: string;
    display_name: string;
    username: string;
    bg_color: string;
    profileImg?: string;
    generation?: number;
  };
  list: Array<{
    id: string;
    name: string;
    rank: number;
    imagePath?: string;
    points?: number;
    avg_rank?: number;
    num_lists?: number;
    user_list_item_id?: string;
    placementVoteTotals?: ListItemVoteTotals;
    currentUserPlacementVote?: ListItemVoteType | null;
  }>;
}

const ListPage = () => {
  const { listid } = useParams();
  const navigate = useNavigate();
  //const { user } = useContext(UserContext);
  const [listData, setListData] = useState<ListData | null>(null);
  // const [isLiked, setIsLiked] = useState(false);
  // const [likeCount, setLikeCount] = useState(0);

  useEffect(() => {
    const asyncGetList = async () => {
      try {
        const listResults = await GetUsersListById(listid);
        console.log("List data:", listResults);

        // Transform data to match our interface
        const transformedData: ListData = {
          id: listResults.list[0]?.id || listid || "",
          title:
            listResults.list[0]?.listtype_name ||
            listResults.list[0]?.category_description ||
            `Top 10 ${listResults.list[0]?.category_name || "Items"}`,
          description: listResults.list[0]?.details || "",
          created: listResults.createdAt || "",
          reactions: listResults.engagement?.reactionCount || 0,
          views: Math.floor(Math.random() * 10000) + 1000, // Mock data for now - TODO: implement real view tracking
          engagement: listResults.engagement || {
            reactionCount: 0,
            engagementScore: 0,
            listVotes: 0,
            listItemVotes: 0,
            comments: 0,
          },
          // Enhanced score data
          listScore: listResults.listScore || 50,
          listScoreLabel:
            listResults.listScoreLabel ||
            getListScoreLabel(
              listResults.listScore || 50,
              listResults.listVoteTotals?.total || 0,
            ),
          listVoteTotals: listResults.listVoteTotals || {
            valid: 0,
            debatable: 0,
            trash: 0,
            total: 0,
          },
          listVotePercentages: listResults.listVotePercentages || {
            valid: 0,
            debatable: 0,
            trash: 0,
          },
          currentUserListVote: listResults.currentUserListVote || null,
          // Backward compatibility
          voteTotals: listResults.voteTotals || {
            valid: 0,
            debatable: 0,
            trash: 0,
          },
          currentUserVote: listResults.currentUserVote || null,
          user: {
            id: listResults.user?.user_id || "",
            display_name: listResults.user?.display_name || "Unknown User",
            username: listResults.user?.display_name || "unknown",
            bg_color: listResults.user?.bg_color || "#3B82F6",
            profileImg: listResults.user?.profileImg || null,
            generation: listResults.user?.generation || null,
          },
          list:
            listResults.list?.map((item: any, index: number) => ({
              id: item.item_id || "",
              name: item.item_name || "Unknown Item",
              rank: index + 1,
              imagePath: item.imagePath || "",
              points: item.points || 0,
              avg_rank: item.avg_rank || 0,
              num_lists: item.num_lists || 0,
              user_list_item_id: item.user_list_item_id || "",
              placementVoteTotals: item.placementVoteTotals || {
                tooHigh: 0,
                rightSpot: 0,
                tooLow: 0,
              },
              currentUserPlacementVote: item.currentUserPlacementVote || null,
            })) || [],
        };

        setListData(transformedData);
        // setLikeCount(transformedData.engagement?.reactionCount || 0);
      } catch (error) {
        console.error("Error fetching list:", error);
      }
    };

    if (listid) {
      asyncGetList();
    }
  }, [listid]);

  /* const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount((prev) => (isLiked ? prev - 1 : prev + 1));
    // TODO: Implement actual like API call
  }; */

  /*  const handleFollow = () => {
    // TODO: Implement follow functionality
    console.log("Follow user");
  }; */

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: listData?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const handleListVoteUpdate = (
    totals: ListVoteTotals,
    userVote: ListVoteType,
    enhancedData?: {
      listScore: number;
      listScoreLabel: string;
      listVoteTotals: ListVoteEnhancedTotals;
      listVotePercentages: ListVotePercentages;
    },
  ) => {
    if (listData) {
      setListData({
        ...listData,
        voteTotals: totals,
        currentUserVote: userVote,
        // Update enhanced score data if provided
        ...(enhancedData && {
          listScore: enhancedData.listScore,
          listScoreLabel: enhancedData.listScoreLabel,
          listVoteTotals: enhancedData.listVoteTotals,
          listVotePercentages: enhancedData.listVotePercentages,
          currentUserListVote: userVote,
        }),
      });
    }
  };

  const handleListItemVoteUpdate = (
    itemId: string,
    totals: ListItemVoteTotals,
    userVote: ListItemVoteType,
  ) => {
    if (listData) {
      setListData({
        ...listData,
        list: listData.list.map((item) =>
          item.user_list_item_id === itemId
            ? {
                ...item,
                placementVoteTotals: totals,
                currentUserPlacementVote: userVote,
              }
            : item,
        ),
      });
    }
  };

  /*   const handleCompare = (itemId: string) => {
    // TODO: Implement compare functionality
    console.log("Compare item:", itemId);
  }; */

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "1 day ago";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (!listData) {
    return <PageLoading />;
  }

  //const isOwnList = user?.isloggedin && user?.id === listData.user.id;

  return (
    <>
      <Helmet>
        <title>{listData.title} - 10GOATS</title>
        <meta
          property="og:title"
          content={`${listData.title} by ${listData.user.display_name}`}
        />
        <meta
          property="og:description"
          content={`Check out ${listData.user.display_name}'s top 10 list on 10GOATS!`}
        />
        <meta
          property="og:image"
          content="https://10goats.com/ogimage.jpg?v=4"
        />
        <meta property="og:url" content={`https://10goats.com/l/${listid}`} />
      </Helmet>

      <Header />

      <div className="min-h-screen">
        <div className="mx-auto max-w-6xl px-4">
          {/* List Hero Section */}
          <div className="p-8 ">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Profile Image Column */}
              <div className="flex-shrink-0 flex justify-center md:justify-start">
                <a
                  href={`/u/${listData.user.display_name}`}
                  className="block rounded-full transition-all duration-200"
                >
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-gray-700">
                    {listData.user.profileImg ? (
                      <img
                        src={listData.user.profileImg}
                        alt={`${listData.user.display_name}'s profile`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={defaultpfp}
                        alt={`${listData.user.display_name}'s profile`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                </a>
              </div>

              {/* List Details Column */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                  <div className="flex-1">
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {listData.user.display_name}'s {listData.title}
                    </h1>
                    {listData.user.generation &&
                      generations[listData.user.generation - 1] && (
                        <span className="inline-block text-xs font-bold uppercase tracking-wider bg-blue-600/20 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded-full mb-3">
                          {generations[listData.user.generation - 1]}
                        </span>
                      )}
                    <div className="flex items-center justify-center md:justify-start space-x-4 mb-4">
                      {/* <span className="text-gray-300">
                        by{" "}
                        <a
                          href={`/u/${listData.user.display_name}`}
                          className="text-blue-400 hover:text-blue-300 transition-colors duration-200 hover:underline font-semibold"
                        >
                          {listData.user.display_name}
                        </a>
                      </span> 
                      <span className="text-gray-500">•</span>*/}
                      <span className="text-gray-400">
                        {listData?.engagement && (
                          <ListEngagement
                            engagement={listData.engagement}
                            className=""
                            showDetails={false}
                          />
                        )}
                      </span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-400">
                        Updated {formatDate(listData.created)}
                      </span>
                      <span className="text-gray-500">•</span>
                      <button
                        onClick={handleShare}
                        className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                      >
                        <ShareIcon className="w-4 h-4 text-gray-300" />
                      </button>
                      {/* <div className="flex items-center space-x-1">
                        <EyeIcon className="w-4 h-4" />
                        <span>{formatNumber(listData.views)} views</span>
                      </div> */}
                    </div>
                    {listData.description && (
                      <p className="text-gray-300 text-lg">
                        {listData.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-center md:justify-start space-x-3 mt-4 md:mt-0">
                    {/* <button
                        onClick={handleLike}
                        className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                      >
                        {isLiked ? (
                          <HeartIconSolid className="w-5 h-5 text-red-500" />
                        ) : (
                          <HeartIcon className="w-5 h-5 text-gray-300" />
                        )}
                        <span className="text-white">
                          {formatNumber(likeCount)}
                        </span>
                      </button>
                      <button
                        onClick={handleFollow}
                        className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
                      >
                        <UserPlusIcon className="w-5 h-5" />
                        <span>Follow</span>
                      </button>
                       */}

                    {/* List Score Badge */}
                    {listData.listScore !== undefined &&
                      listData.listVoteTotals && (
                        <ListScoreBadge
                          score={listData.listScore}
                          label={listData.listScoreLabel || "Unrated"}
                          totalVotes={listData.listVoteTotals.total}
                          className="ml-4"
                        />
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="separator-fade" />

          {/* Primary CTA */}
          <div className="text-center my-12">
            <button onClick={() => navigate("/lists")} className="btn">
              Create Your Own Top 10 List
            </button>
          </div>
          <div className="separator-fade" />

          {/* Ranked List Section */}
          <div className="space-y-4 mb-12">
            {listData.list.map((item) => (
              <div
                key={item.id}
                className="bg-gray-900/10 border border-gray-700 rounded-xl overflow-hidden hover:border-yellow-500 transition-all duration-200"
              >
                <div className="flex h-48 md:h-36">
                  {/* Artist Image with Floating Rank */}
                  <div className="relative flex-shrink-0 w-32 h-48 md:h-36">
                    {item.imagePath ? (
                      <img
                        src={`${item.imagePath}`}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                        <span className="text-4xl font-bold text-white">
                          {item.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    {/* Floating Rank Badge */}
                    <div className="absolute top-2 left-2 w-8 h-8 bg-yellow-500 text-black font-bold text-md rounded-full flex items-center justify-center shadow-md shadow-black/50">
                      #{item.rank}
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 p-4 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-2 truncate">
                        <a
                          href={`/rapper/${createSlug(item.name)}`}
                          className="text-white hover:text-blue-400 transition-colors duration-200"
                        >
                          {item.name}
                        </a>
                      </h3>
                      {(item.points || item.avg_rank || item.num_lists) && (
                        <div className="flex flex-row space-x-6">
                          {item.points > 0 && (
                            <p className="text-xs md:text-md">
                              <span className="hidden md:inline">Total</span>{" "}
                              Points: {formatNumber(item.points)}
                            </p>
                          )}
                          {item.avg_rank > 0 && (
                            <p className="text-xs md:text-md">
                              Avg <span className="hidden md:inline">Rank</span>
                              : #{item.avg_rank}
                            </p>
                          )}
                          {item.num_lists > 0 && (
                            <p className="text-xs md:text-md">
                              <span className="hidden md:inline">
                                Appears in {item.num_lists} lists
                              </span>
                              <span className="md:hidden">
                                Lists: {item.num_lists} lists
                              </span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Item Placement Voting */}
                    {item.placementVoteTotals && item.user_list_item_id && (
                      <ListItemVoteBar
                        listId={listData.id}
                        listItemId={item.user_list_item_id}
                        placementVoteTotals={item.placementVoteTotals}
                        currentUserPlacementVote={item.currentUserPlacementVote}
                        rank={item.rank}
                        totalItems={listData.list.length}
                        onVoteUpdate={(totals, userVote) =>
                          handleListItemVoteUpdate(
                            item.user_list_item_id!,
                            totals,
                            userVote,
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {listData.voteTotals && (
            <ListVoteBar
              listId={listData.id}
              voteTotals={listData.voteTotals}
              currentUserVote={listData.currentUserVote}
              onVoteUpdate={handleListVoteUpdate}
            />
          )}
          <div className="separator-fade" />
          {/* Comments Section */}
          <CommentSection
            targetType="list"
            targetId={listData.id}
            className="mb-8"
          />

          {/* Bottom CTA */}
          <div className="text-center bg-gray-900/10 border border-gray-700 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-white mb-4">
              Fight for your Top 10. Prove you're a GOAT.
            </h2>
            <button onClick={() => navigate("/create")} className="btn">
              Rank the GOATs
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ListPage;
